import { useParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, ArrowLeft, CheckCircle, Calendar, Users } from "lucide-react";

interface Course {
  id: string;
  title: string;
  description: string;
  fullDescription: string;
  duration: string;
  status: "available" | "coming-soon" | "new" | "featured";
  image: string;
  category: string;
  highlights: string[];
  registrationUrl: string;
}

const courses: Record<string, Course> = {
  "data-lifecycle-storytelling-workshop": {
    id: "data-lifecycle-storytelling-workshop",
    title: "Data Life Cycle & Storytelling Workshop",
    description: "Learn the full data journey from real experts.",
    fullDescription: "Learn the full data journey from real experts: Data Engineers, Data Scientists, and Data Analysts. This intensive workshop provides hands-on knowledge transfer from field professionals, covering the complete data lifecycle from collection to visualization.",
    duration: "2 days intensive",
    status: "new",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=600&fit=crop",
    category: "Workshop",
    highlights: ["Hands-on exercises", "Real-world case studies", "Expert instructors", "Certificate of completion"],
    registrationUrl: "https://fasttrack-academy.lovable.app/course/data-lifecycle-storytelling-workshop"
  },
  "fasttrack-development-program": {
    id: "fasttrack-development-program",
    title: "FTS Development Summer Program",
    description: "Rotate through all major project departments.",
    fullDescription: "Rotate through all major project departments and stages — from initiation to closing — guided by real industry experts. This comprehensive 8-week program gives you hands-on experience in every aspect of project management.",
    duration: "8 weeks",
    status: "featured",
    image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200&h=600&fit=crop",
    category: "Program",
    highlights: ["Full project lifecycle exposure", "Industry mentorship", "Team collaboration", "Portfolio building"],
    registrationUrl: "https://fasttrack-academy.lovable.app/course/fasttrack-development-program"
  },
  "power-automate-workflows": {
    id: "power-automate-workflows",
    title: "Power Automate Workflows for Project Management",
    description: "Create automated workflows using Microsoft Power Automate.",
    fullDescription: "Learn how to create automated workflows using Microsoft Power Automate to streamline project management processes and increase team productivity. Master triggers, actions, and conditions to build powerful automation solutions.",
    duration: "3 weeks",
    status: "available",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=600&fit=crop",
    category: "Power Platform",
    highlights: ["Cloud flows", "Approval workflows", "Email automation", "Integration with Microsoft 365"],
    registrationUrl: "https://fasttrack-academy.lovable.app/course/power-automate-workflows"
  },
  "data-journey-power-bi": {
    id: "data-journey-power-bi",
    title: "Data Journey by Power BI",
    description: "Master data analysis and visualization.",
    fullDescription: "Master data analysis and visualization using Microsoft Power BI to transform raw data into actionable insights, interactive dashboards, and compelling visual stories that drive business decisions.",
    duration: "5 weeks",
    status: "available",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=600&fit=crop",
    category: "Power Platform",
    highlights: ["DAX formulas", "Custom visualizations", "Data modeling", "Dashboard design"],
    registrationUrl: "https://fasttrack-academy.lovable.app/course/data-journey-power-bi"
  },
  "data-transformation-advanced": {
    id: "data-transformation-advanced",
    title: "Data Transformation with Advanced Queries",
    description: "Master data transformation techniques.",
    fullDescription: "Master data transformation techniques using advanced queries to clean, reshape, and analyze project data for better decision-making. Learn Power Query M language and advanced ETL techniques.",
    duration: "4 weeks",
    status: "available",
    image: "https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=1200&h=600&fit=crop",
    category: "Data",
    highlights: ["Power Query M", "Data cleansing", "ETL processes", "Advanced transformations"],
    registrationUrl: "https://fasttrack-academy.lovable.app/course/data-transformation-advanced"
  },
  "project-digital-transformation": {
    id: "project-digital-transformation",
    title: "Power Platform End to End Solution",
    description: "Leverage Microsoft Power Platform for digital transformation.",
    fullDescription: "Learn how to leverage Microsoft Power Platform and advanced 360 virtual reporting to transform project planning and management workflows for increased efficiency and better outcomes. Includes comprehensive training on creating immersive virtual tours.",
    duration: "6 weeks",
    status: "featured",
    image: "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1200&h=600&fit=crop",
    category: "Power Platform",
    highlights: ["End-to-end solutions", "360° virtual tours", "Power Apps", "Integration strategies"],
    registrationUrl: "https://fasttrack-academy.lovable.app/course/project-digital-transformation"
  },
  "ai-agent-development-n8n": {
    id: "ai-agent-development-n8n",
    title: "AI Agent Development with N8N",
    description: "Build intelligent automation workflows.",
    fullDescription: "Build intelligent automation workflows and AI agents using N8N's powerful visual workflow builder. Learn to create sophisticated bots that can handle complex tasks and integrate with multiple services.",
    duration: "3 weeks",
    status: "available",
    image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&h=600&fit=crop",
    category: "AI & Automation",
    highlights: ["Visual workflow builder", "AI integrations", "API connections", "Bot development"],
    registrationUrl: "https://fasttrack-academy.lovable.app/course/ai-agent-development-n8n"
  },
  "ai-digital-transformation-integrated": {
    id: "ai-digital-transformation-integrated",
    title: "AI Agent + Digital Transformation Integrated Program",
    description: "Comprehensive AI and Power Platform program.",
    fullDescription: "Comprehensive program combining AI Agent Development with N8N and Digital Transformation using Power Platform. Learn to build intelligent automation workflows while transforming project planning processes with advanced 360 virtual reporting capabilities.",
    duration: "9 weeks",
    status: "available",
    image: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=1200&h=600&fit=crop",
    category: "AI & Automation",
    highlights: ["Combined curriculum", "AI + Power Platform", "Advanced automation", "Comprehensive certification"],
    registrationUrl: "https://fasttrack-academy.lovable.app/course/ai-digital-transformation-integrated"
  }
};

const getStatusBadge = (status: Course["status"]) => {
  switch (status) {
    case "new":
      return <Badge className="bg-green-500 text-white">NEW WORKSHOP</Badge>;
    case "featured":
      return <Badge className="bg-primary text-primary-foreground">FEATURED</Badge>;
    case "coming-soon":
      return <Badge variant="secondary">COMING SOON</Badge>;
    default:
      return <Badge variant="outline">AVAILABLE</Badge>;
  }
};

const CourseDetail = () => {
  const { id } = useParams<{ id: string }>();
  const course = id ? courses[id] : null;

  if (!course) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground mb-4">Course Not Found</h1>
            <Link to="/courses">
              <Button>Back to Courses</Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1">
        {/* Hero Image */}
        <div className="relative h-64 sm:h-80 lg:h-96 overflow-hidden">
          <img
            src={course.image}
            alt={course.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent" />
          <div className="absolute bottom-6 left-0 right-0">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <Link to="/courses" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
                <ArrowLeft className="h-4 w-4" />
                Back to Courses
              </Link>
              <div className="flex items-center gap-3 mb-2">
                {getStatusBadge(course.status)}
                <span className="text-sm text-muted-foreground">{course.category}</span>
              </div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground">
                {course.title}
              </h1>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <h2 className="text-xl font-semibold text-foreground mb-4">About This Course</h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                {course.fullDescription}
              </p>

              <h2 className="text-xl font-semibold text-foreground mb-4">What You Will Learn</h2>
              <ul className="space-y-3 mb-8">
                {course.highlights.map((highlight, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{highlight}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-card border border-border rounded-lg p-6 sticky top-20">
                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Duration</p>
                      <p className="font-medium text-foreground">{course.duration}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Status</p>
                      <p className="font-medium text-foreground">Registration Open</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Format</p>
                      <p className="font-medium text-foreground">Online / Hybrid</p>
                    </div>
                  </div>
                </div>

                <a href={course.registrationUrl} target="_blank" rel="noopener noreferrer">
                  <Button className="w-full" size="lg">
                    Register Now
                  </Button>
                </a>
                <p className="text-xs text-muted-foreground text-center mt-3">
                  Registration on FastTrack Academy
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CourseDetail;
